/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import uniq from 'lodash.uniq';
import {Environment, get, getEnvironment} from '@ion/core/src/environment/environment';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink} from '@ion/core/src/types/entities';

// accounts whose geofences are limited to the global group's immediate sub-accounts,
// by environment - account ids are only unique within an environment (87952 is
// RAC Group in EU production) ...
export const IMMEDIATE_SUB_ACCOUNTS_ONLY_IDS: Partial<Record<Environment, number[]>> = {
  [Environment.eu] : [87952]
};

/**
 * Returns the environment of the backend the application is configured against
 * (environment.json's ctcURL) - unlike the browser host, this also identifies the
 * environment when running locally against a remote backend (e.g. EU) ...
 */
export const getConfiguredEnvironment = (): Environment => {
  const ctcURL = get('ctcURL');
  return getEnvironment(ctcURL ? new URL(ctcURL).host : undefined);
};

/**
 * Returns `true` when the given account is limited to its immediate sub-accounts
 * in the environment of the backend the application is configured against.
 */
export const isImmediateSubAccountsOnly = (
  accountId: number,
  environment: Environment = getConfiguredEnvironment()
): boolean => (
  Boolean(IMMEDIATE_SUB_ACCOUNTS_ONLY_IDS[environment]?.includes(accountId))
);

/**
 * Returns the ids of the global group's immediate (first level) sub-accounts,
 * preceded by the global group id itself when `includeParent` is `true`.
 */
export const getImmediateSubAccountIds = (
  globalGroup: AccountLink,
  globalGroupId: number,
  includeParent: boolean = true
): number[] => {
  const accountIds = globalGroup?.subAccounts?.map(sub => entityId(sub?.subaccount?.href || sub?.href)) ?? [];
  if (includeParent && globalGroupId) {
    accountIds.unshift(globalGroupId);
  }
  return uniq(accountIds);
};
